import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subscription } from 'rxjs';
import { ChatItemDetailsDto } from 'src/app/data/models/dto/chat-item-details.dto';
import { DataFileDownload } from 'src/app/data/models/dto/data-file-download';
import { FileContentDto } from 'src/app/data/models/dto/file-content.dto';
import { FileDto } from 'src/app/data/models/dto/file.dto';
import { GenericDialogDto } from 'src/app/data/models/dto/generic-dialog.dto';
import { RoomMessageDto } from 'src/app/data/models/dto/room-message.dto';
import { MessageSendStatus } from 'src/app/data/models/enum/message-send-status.enum';
import { RoomMessage } from 'src/app/data/models/interfaces/room-message.interface';
import { UserMessage } from 'src/app/data/models/interfaces/user-message.interface';
import { ChatsGatewayService } from 'src/app/data/services/chats-gateway.service';
import { NetworkService } from 'src/app/data/services/network.service';
import { FileUtils } from 'src/app/data/utils/file.utils';
import { genericDialog } from 'src/app/data/utils/generic-dialog.utils';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { SnackBarComponent } from 'src/app/ui/components/snack-bar/snack-bar.component';

@Component({
  selector: 'app-chats-details-item',
  templateUrl: './chats-details-item.component.html',
  styleUrls: ['./chats-details-item.component.scss'],
})
export class ChatsDetailsItemComponent implements OnInit, OnDestroy {
  @Input() msg: any;
  @Input() itemDetails?: ChatItemDetailsDto;
  @Input() roomMembersLength?: number;
  @Input() idUserFromRoom?: string;

  @Output() onUploadCanceled = new EventEmitter<boolean>();
  @Output() onUploadSuccessed = new EventEmitter<FileDto>();
  @Output() onDeleteMessageClicked = new EventEmitter<boolean>();
  @Output() forwardChange = new EventEmitter<boolean>();
  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  @Output() onSelectForward = new EventEmitter<boolean>();
  @Output() retrySendRoomMessage = new EventEmitter<RoomMessageDto>();
  @Output() retrySendUserMessage = new EventEmitter<UserMessage>();

  messageSendStatus = MessageSendStatus

  contextMenuImage = 'expand_more';
  mouseOn = false;
  contextMenuVisibility = 'hidden';
  recentMessageLimitHour = 2;
  userId = '';

  isForwardChecked = false;

  confirmationDialog: GenericDialogDto = {
    imageUrl: 'assets/images/chat-message-delete-confirmation.svg',
    primaryText: 'Você tem certeza que deseja deletar a mensagem?',
    secondaryText:
      'Ela será deletada para todos neste chat. Além disso, a ação não pode ser desfeita',
    btnCancelText: 'Não, cancelar',
    btnConfirmText: 'Sim, deletar',
  };

  subscriptions: Subscription = new Subscription();

  get responsibleOrStudentHasVisualized(): boolean {
    return this.msg.membersWhoVisualized.includes(this.idUserFromRoom);
  }

  constructor(
    private readonly fileUtils: FileUtils,
    private readonly snackBar: SnackBarComponent,
    private readonly userUtil: UserUtil,
    public dialog: MatDialog,
    private gatewayService: ChatsGatewayService,
    private readonly networkService: NetworkService,
  ) {}

  get isOnline(): boolean {
    return this.networkService.isOnline$.value;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    const user = this.userUtil.getUser();
    this.userId = user.id;
    this.subscriptions.add(this.handleForwardMessageUpdate());
  }

  onClickOutside() {
    this.menuTrigger.closeMenu();
  }

  onTouchOutside() {
    this.mouseOn = false;
    this.setContextMenuVisibility();
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchInside(event: any) {
    this.mouseOn = true;
    this.setContextMenuVisibility();
  }

  setContextMenuVisibility() {
    if (this.menuTrigger) {
      if (this.menuTrigger.menuOpen) this.contextMenuVisibility = 'visible';
      else this.contextMenuVisibility = this.mouseOn ? 'visible' : 'hidden';
    }
  }

  fileExtension(name: string) {
    return this.fileUtils.fileExtension(name);
  }

  filesContentDto: FileContentDto[] = [];
  formatBytes(dataFile: DataFileDownload) {
    let value: string = '';
    this.filesContentDto.forEach((file) => {
      if (
        dataFile.idMessage === file.idMessage &&
        dataFile.fileName === file.fileName
      ) {
        value = this.fileUtils.formatBytes(file.sizeFile);
      }
    });
    return value;
  }

  handleDownloadError() {
    this.snackBar.openSnackBar(`Arquivo não encontrado no servidor`, 'ok');
  }

  private isFileDto(file: string | FileDto): boolean {
    return typeof file === 'object' && 'bucketKey' in file;
  }

  createFileDownload(
    idMessage: string | undefined,
    file: string | FileDto
  ): DataFileDownload | FileDto {
    if (this.isFileDto(file)) {
      return file as FileDto;
    } else {
      const fileDownload: DataFileDownload = {
        entityName: this.itemDetails?.isRoomMessage
          ? 'roomsMessages'
          : 'usersMessages',
        idMessage: idMessage,
        fileName: file as string,
        isActive: false,
      };
      return fileDownload;
    }
  }

  getFileName(file: string | FileDto) {
    if (typeof file === 'string') {
      return file;
    } else {
      return (file as FileDto).name;
    }
  }

  uploadCanceled() {
    this.onUploadCanceled.emit(true);
  }

  retryRoom(msg: RoomMessageDto) {
    this.retrySendRoomMessage.emit(msg)
  }

  retryUser(msg: UserMessage) {
    this.retrySendUserMessage.emit(msg)
  }

  uploadSuccessed(fileDto: FileDto) {
    this.onUploadSuccessed.emit(fileDto);
  }

  showContextMenu(): boolean {
    let dt = new Date();
    dt.setHours(dt.getHours() - this.recentMessageLimitHour);
    const messageCreate = new Date(this.msg.dtMessage!);
    return this.msg.idAuthor == this.userId && messageCreate > dt && !this.msg.shouldNotCreateTicket;
  }

  deleteMessageClick() {
    genericDialog(
      this.dialog,
      this.confirmationDialog,
      'custom-dialog-confirmation'
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.onDeleteMessageClicked.emit(true);
      }
    });
  }

  touchLongPress() {
    this.contextMenuVisibility = 'visible';
    this.menuTrigger.openMenu();
  }

  forwardMessage(value: boolean) {
    this.forwardChange.emit(value);

    const v = new MatCheckboxChange();
    v.checked = value;
    this.selectForward(v);
  }

  get isAuthor() {
    return this.msg.idAuthor === this.userId;
  }

  get showForwardMenu(): boolean {
    return !this.isAuthor && (this.itemDetails?.isRoomMessage || false);
  }

  selectForward(value: MatCheckboxChange) {
    this.isForwardChecked = value.checked;
    this.onSelectForward.emit(value.checked);
  }

  @Input()
  get forward() {
    return this._forward;
  }

  set forward(value: boolean) {
    this._forward = value;
    if (!value) {
      this.isForwardChecked = this._forward;
    }
  }
  private _forward: boolean = false;

  get forwardStatus() {
    const m = this.msg as RoomMessage;
    if (this.itemDetails?.isRoomMessage && m.foward) {
      if (m.id === m.foward?.id) {
        return `Encaminhada para o canal ${m.foward?.toChannel.name}`;
      } else {
        return `Encaminhada do canal ${m.foward?.fromChannel.name}`;
      }
    }
    return null;
  }

  private handleForwardMessageUpdate() {
    return this.gatewayService
      .receiveForwardMessage()
      .subscribe((messageEvent) => {
        if (messageEvent && messageEvent.foward.id === this.msg.id) {
          this.msg.foward = messageEvent.foward;
        }
      });
  }
}
